import React from 'react';
import {
  School, ArrowRight, Users, CalendarDays, ClipboardCheck, Boxes,
  Wallet, MessageSquare, Building2, Smartphone, Lock, Globe,
} from 'lucide-react';
import { useT, LANGUAGES } from '../i18n.jsx';

/**
 * What the bare domain shows.
 *
 * It used to list every school on the platform, with each one's name, badge and address. That list
 * was the single thing most at odds with what a school is being sold: its own portal. A principal
 * who saw it learned in one glance that theirs is one row in somebody else's table.
 *
 * So the root is now the product's own page, and it names no school. A school is reached only at its
 * own address -- a subdomain, or a domain the school points here itself -- typed into the address
 * bar, which is how somebody who has a portal already opens it. There is no way in from this page,
 * on purpose: a box that accepts a school name is a box worth guessing at.
 *
 * Nothing is read from Firestore here at all, so there is no list to leak.
 */

const MODULE_GROUPS = [
  { key: 'people', icon: Users },
  { key: 'daily', icon: ClipboardCheck },
  { key: 'timetable', icon: CalendarDays },
  { key: 'campus', icon: Boxes },
  { key: 'finance', icon: Wallet },
  { key: 'contact', icon: MessageSquare },
];

const OWN_APP_POINTS = [
  { key: 'address', icon: Globe },
  { key: 'homeScreen', icon: Smartphone },
  { key: 'data', icon: Lock },
];

const LandingPage = () => {
  const { t, language, setLanguage } = useT();

  const section = { maxWidth: '1040px', margin: '0 auto', padding: '0 1.5rem' };
  const card = {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow-sm)',
  };

  return (
    <div className="animate-fade-in" style={{ backgroundColor: 'var(--color-bg-app)', minHeight: '100vh' }}>

      {/* ---------------------------------------------------------------- masthead */}
      <header style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}>
        <div style={{
          ...section,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '1rem', paddingTop: '0.9rem', paddingBottom: '0.9rem', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <div style={{
              backgroundColor: 'var(--color-primary)', width: '36px', height: '36px',
              borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--color-text-on-primary)',
            }}>
              <School size={20} />
            </div>
            <span style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--color-text-main)' }}>
              {t('landing.product')}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.35rem' }}>
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => setLanguage(l.code)}
                aria-pressed={language === l.code}
                style={{
                  padding: '0.3rem 0.7rem',
                  fontSize: '0.78rem',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontWeight: language === l.code ? '700' : '500',
                  backgroundColor: 'transparent',
                  border: `1px solid ${language === l.code ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  color: language === l.code ? 'var(--color-primary)' : 'var(--color-text-muted)',
                }}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ---------------------------------------------------------------- hero */}
      <div style={{ ...section, paddingTop: '4rem', paddingBottom: '3.5rem', textAlign: 'center' }}>
        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: '800', letterSpacing: '-0.025em',
          color: 'var(--color-text-main)', marginBottom: '1rem', lineHeight: '1.2',
        }}>
          {t('landing.headline')}
        </h1>
        <p style={{
          fontSize: '1.125rem', color: 'var(--color-text-muted)',
          maxWidth: '620px', margin: '0 auto', lineHeight: '1.7',
        }}>
          {t('landing.subhead')}
        </p>

      </div>

      {/* ---------------------------------------------------------------- the pitch */}
      <div style={{ backgroundColor: 'var(--color-bg-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ ...section, paddingTop: '3.5rem', paddingBottom: '3.5rem' }}>
          <h2 style={{
            fontSize: '1.6rem', fontWeight: '700', color: 'var(--color-text-main)',
            textAlign: 'center', marginBottom: '0.75rem',
          }}>
            {t('landing.ownApp.title')}
          </h2>
          <p style={{
            color: 'var(--color-text-muted)', textAlign: 'center',
            maxWidth: '620px', margin: '0 auto 2.5rem', lineHeight: '1.7',
          }}>
            {t('landing.ownApp.intro')}
          </p>

          <div style={{
            display: 'grid', gap: '1.25rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
          }}>
            {OWN_APP_POINTS.map(({ key, icon: Icon }) => (
              <div key={key} style={card}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.9rem',
                }}>
                  <Icon size={20} />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--color-text-main)', marginBottom: '0.4rem' }}>
                  {t(`landing.ownApp.points.${key}.title`)}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', lineHeight: '1.65' }}>
                  {t(`landing.ownApp.points.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------------- what is inside */}
      <div style={{ ...section, paddingTop: '3.5rem', paddingBottom: '3.5rem' }}>
        <h2 style={{
          fontSize: '1.6rem', fontWeight: '700', color: 'var(--color-text-main)',
          textAlign: 'center', marginBottom: '0.75rem',
        }}>
          {t('landing.modules.title')}
        </h2>
        <p style={{
          color: 'var(--color-text-muted)', textAlign: 'center',
          maxWidth: '620px', margin: '0 auto 2.5rem', lineHeight: '1.7',
        }}>
          {t('landing.modules.intro')}
        </p>

        <div style={{
          display: 'grid', gap: '1.25rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
        }}>
          {MODULE_GROUPS.map(({ key, icon: Icon }) => (
            <div key={key} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.7rem' }}>
                <Icon size={18} color="var(--color-primary)" />
                <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--color-text-main)' }}>
                  {t(`landing.modules.groups.${key}.title`)}
                </h3>
              </div>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', lineHeight: '1.7' }}>
                {t(`landing.modules.groups.${key}.body`)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------------------- for a school not yet here */}
      <div style={{ backgroundColor: 'var(--color-bg-subtle)', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ ...section, paddingTop: '3.5rem', paddingBottom: '3.5rem', textAlign: 'center' }}>
          <Building2 size={28} color="var(--color-primary)" style={{ marginBottom: '0.9rem' }} />
          <h2 style={{ fontSize: '1.6rem', fontWeight: '700', color: 'var(--color-text-main)', marginBottom: '0.75rem' }}>
            {t('landing.cta.title')}
          </h2>
          <p style={{
            color: 'var(--color-text-muted)', maxWidth: '620px',
            margin: '0 auto 1.5rem', lineHeight: '1.7',
          }}>
            {t('landing.cta.body')}
          </p>
          <a
            href={`mailto:${t('landing.cta.email')}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.7rem 1.4rem', fontSize: '0.95rem', fontWeight: '600',
              borderRadius: 'var(--radius-md)', textDecoration: 'none',
              backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)',
            }}
          >
            {t('landing.cta.action')}
            <ArrowRight size={16} />
          </a>
        </div>
      </div>

      <footer style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}>
        <div style={{
          ...section, paddingTop: '1.5rem', paddingBottom: '1.5rem',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: '1rem', flexWrap: 'wrap',
          fontSize: '0.82rem', color: 'var(--color-text-subtle)',
        }}>
          {/* The developer portal is deliberately not linked from anywhere public. Naming it here
              would tell every visitor that a door above their school exists, and where it is. */}
          <span>{t('landing.footer.line')}</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
